import React, { useState, useCallback } from 'react';
import {
  SurveyAnalysis,
  SchoolInsights,
  CrossSurveyComparison,
  TextResponses,
} from './question-breakdown';
import { AdminFilters, FilterState, useAdminContext } from './shared';
import './QuestionBreakdowns.css';

type ViewMode = 'survey' | 'schools' | 'cross-survey' | 'text';

const QuestionBreakdowns: React.FC = () => {
  const { respondentType } = useAdminContext();
  const [viewMode, setViewMode] = useState<ViewMode>('survey');
  const [filters, setFilters] = useState<FilterState>({
    surveyPeriod: '2026-q1',
    school: '',
    surveyType: '6-8',
    surveyTypes: ['k-2', '3-5', '6-8', 'high-school', 'virtual-k8', 'virtual-hs'],
    respondentType: 'student',
  });

  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  // If on cross-survey tab and respondent type changes away from student, switch to survey tab
  const effectiveViewMode =
    viewMode === 'cross-survey' && respondentType !== 'student' ? 'survey' : viewMode;

  const renderView = () => {
    switch (effectiveViewMode) {
      case 'survey':
        return (
          <SurveyAnalysis
            surveyType={filters.surveyType}
            school={filters.school || undefined}
            respondentType={respondentType}
          />
        );
      case 'schools':
        return <SchoolInsights surveyType={filters.surveyType} respondentType={respondentType} />;
      case 'cross-survey':
        return <CrossSurveyComparison respondentType={respondentType} />;
      case 'text':
        return <TextResponses respondentType={respondentType} />;
      default:
        return null;
    }
  };

  return (
    <div className="question-breakdowns-page">
      <div className="qb-header">
        <div>
          <h2>Question Analysis</h2>
          <p className="qb-subtitle">
            {effectiveViewMode === 'survey' && 'Category-based analysis with question drill-down'}
            {effectiveViewMode === 'schools' && 'Compare schools within a survey type'}
            {effectiveViewMode === 'cross-survey' && 'Compare categories across all survey types'}
            {effectiveViewMode === 'text' && `Browse open-ended ${respondentType} feedback`}
          </p>
        </div>
      </div>

      <div className="qb-nav">
        <div className="qb-view-tabs">
          <button
            className={`qb-tab ${effectiveViewMode === 'survey' ? 'active' : ''}`}
            onClick={() => setViewMode('survey')}
          >
            Survey Analysis
          </button>
          <button
            className={`qb-tab ${effectiveViewMode === 'schools' ? 'active' : ''}`}
            onClick={() => setViewMode('schools')}
          >
            School Insights
          </button>
          {respondentType === 'student' && (
            <button
              className={`qb-tab ${effectiveViewMode === 'cross-survey' ? 'active' : ''}`}
              onClick={() => setViewMode('cross-survey')}
            >
              Cross-Survey
            </button>
          )}
          <button
            className={`qb-tab ${effectiveViewMode === 'text' ? 'active' : ''}`}
            onClick={() => setViewMode('text')}
          >
            Text Responses
          </button>
        </div>

        {(effectiveViewMode === 'survey' || effectiveViewMode === 'schools') && (
          <AdminFilters
            onFiltersChange={handleFiltersChange}
            showSurveyPeriod={true}
            respondentType={respondentType}
          />
        )}
      </div>

      <div className="qb-content">
        {renderView()}
      </div>
    </div>
  );
};

export default QuestionBreakdowns;
